import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  SimpleGrid,
  Stack,
  Divider,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from '@chakra-ui/icons';
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  getDaysInMonth,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
  setYear,
  setMonth,
} from 'date-fns';
import { cs } from 'date-fns/locale';

export type DateRangePickerProps = {
  from?: string;
  to?: string;
  onChange: (from: string, to: string) => void;
  size?: 'sm' | 'md';
};

function parseDate(value?: string | null): Date | null {
  if (!value) return null;
  try {
    const d = parseISO(value);
    if (!isNaN(d.getTime())) return d;
  } catch {
    // ignore
  }
  return null;
}

function toIsoDate(d: Date | null | undefined): string {
  if (!d || isNaN(d.getTime())) return '';
  return format(d, 'yyyy-MM-dd');
}

const monthNamesCs = [
  'Leden',
  'Únor',
  'Březen',
  'Duben',
  'Květen',
  'Červen',
  'Červenec',
  'Srpen',
  'Září',
  'Říjen',
  'Listopad',
  'Prosinec',
];

const weekdayLabelsCs = (() => {
  const start = startOfWeek(new Date(), { weekStartsOn: 1 });
  return Array.from({ length: 7 }).map((_, i) =>
    format(addDays(start, i), 'EEEEE', { locale: cs }),
  );
})();

const buildPresets = () => {
  const today = new Date();
  const todayIso = toIsoDate(today);

  const lastNDays = (n: number) => {
    const end = today;
    const start = addDays(end, -n + 1);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  const thisWeek = () => {
    const monday = startOfWeek(today, { weekStartsOn: 1 });
    const sunday = endOfWeek(today, { weekStartsOn: 1 });
    return { from: toIsoDate(monday), to: toIsoDate(sunday) };
  };

  const next30Days = () => {
    const start = today;
    const end = addDays(start, 30);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  const thisMonth = () => {
    const start = startOfMonth(today);
    const end = endOfMonth(today);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  const last3Months = () => {
    const end = today;
    const start = addMonths(startOfMonth(end), -2);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  const last12Months = () => {
    const end = today;
    const start = addMonths(startOfMonth(end), -11);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  const thisYear = () => {
    const year = today.getFullYear();
    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    return { from: toIsoDate(start), to: toIsoDate(end) };
  };

  return [
    { key: 'today', label: 'Dnes', getRange: () => ({ from: todayIso, to: todayIso }) },
    { key: 'last7', label: 'Posledních 7 dní', getRange: () => lastNDays(7) },
    { key: 'last30', label: 'Posledních 30 dní', getRange: () => lastNDays(30) },
    { key: 'thisWeek', label: 'Tento týden', getRange: () => thisWeek() },
    { key: 'next30', label: 'Nadcházejících 30 dní', getRange: () => next30Days() },
    { key: 'thisMonth', label: 'Tento měsíc', getRange: () => thisMonth() },
    { key: 'last3months', label: 'Poslední 3 měsíce', getRange: () => last3Months() },
    { key: 'last12months', label: 'Posledních 12 měsíců', getRange: () => last12Months() },
    { key: 'thisYear', label: 'Tento rok', getRange: () => thisYear() },
  ];
};

const PRESETS = buildPresets();

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ from, to, onChange, size = 'sm' }) => {
  const [viewDate, setViewDate] = useState<Date>(() => parseDate(from) || parseDate(to) || new Date());
  const [draftFrom, setDraftFrom] = useState<Date | null>(() => parseDate(from));
  const [draftTo, setDraftTo] = useState<Date | null>(() => parseDate(to));
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [activePanel, setActivePanel] = useState<'calendar' | 'year' | 'month' | 'day'>('calendar');
  const [yearPageStart, setYearPageStart] = useState<number>(() => {
    const base = (parseDate(from) || parseDate(to) || new Date()).getFullYear();
    return base - 6; // show 12 years around current
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const sideBg = useColorModeValue('gray.50', 'gray.900');
  const sideActiveBg = useColorModeValue('blue.50', 'blue.900');
  const sideActiveColor = useColorModeValue('blue.700', 'blue.100');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const dayBgSelected = useColorModeValue('blue.500', 'blue.400');
  const dayBgInRange = useColorModeValue('blue.50', 'blue.900');
  const dayTextSelected = useColorModeValue('white', 'gray.900');
  const dayTextSubtle = useColorModeValue('gray.400', 'gray.500');
  const triggerBg = useColorModeValue('white', 'gray.800');
  const triggerHoverBg = useColorModeValue('gray.50', 'gray.700');
  const triggerBorderColor = useColorModeValue('gray.300', 'gray.600');

  useEffect(() => {
    const start = parseDate(from);
    const end = parseDate(to);
    setDraftFrom(start);
    setDraftTo(end);
    const base = start || end || new Date();
    setViewDate(base);
    setYearPageStart(base.getFullYear() - 6);
  }, [from, to]);

  const rangeLabel = useMemo(() => {
    const start = parseDate(from);
    const end = parseDate(to);
    if (start && end) {
      return `${format(start, 'd.M.yyyy', { locale: cs })} – ${format(end, 'd.M.yyyy', { locale: cs })}`;
    }
    if (start) return `Od ${format(start, 'd.M.yyyy', { locale: cs })}`;
    if (end) return `Do ${format(end, 'd.M.yyyy', { locale: cs })}`;
    return 'Libovolné období';
  }, [from, to]);

  const handleDayClick = (day: Date) => {
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(day);
      setDraftTo(null);
      return;
    }
    if (draftFrom && !draftTo) {
      if (day < draftFrom) {
        setDraftTo(draftFrom);
        setDraftFrom(day);
      } else if (day.getTime() === draftFrom.getTime()) {
        // Single day toggle
        setDraftTo(day);
      } else {
        setDraftTo(day);
      }
    }
  };

  const currentRange = useMemo(() => {
    if (!draftFrom && !draftTo) return null;
    if (draftFrom && draftTo) {
      return draftFrom <= draftTo
        ? { start: draftFrom, end: draftTo }
        : { start: draftTo, end: draftFrom };
    }
    if (draftFrom && hoverDate) {
      return draftFrom <= hoverDate
        ? { start: draftFrom, end: hoverDate }
        : { start: hoverDate, end: draftFrom };
    }
    return null;
  }, [draftFrom, draftTo, hoverDate]);

  const monthStart = startOfMonth(viewDate);
  const monthEnd = endOfMonth(viewDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const calendarDays: Date[] = [];
  for (let d = calendarStart; d <= calendarEnd; d = addDays(d, 1)) {
    calendarDays.push(d);
  }

  const weeks: Date[][] = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  const yearOptions = useMemo(() => {
    return Array.from({ length: 12 }).map((_, i) => yearPageStart + i);
  }, [yearPageStart]);

  const selectedReferenceDate = draftFrom || draftTo || viewDate;
  const selectedYear = selectedReferenceDate.getFullYear();
  const selectedMonth = selectedReferenceDate.getMonth();
  const daysInSelectedMonth = getDaysInMonth(new Date(selectedYear, selectedMonth, 1));
  const headerDayLabel = format(selectedReferenceDate, 'd.', { locale: cs });

  const applyDraft = () => {
    const nextFrom = toIsoDate(draftFrom || null);
    const nextTo = toIsoDate(draftTo || draftFrom || null);
    onChange(nextFrom, nextTo);
  };

  const clearDraft = () => {
    setDraftFrom(null);
    setDraftTo(null);
    setHoverDate(null);
  };

  const renderCalendar = () => (
    <VStack align="stretch" spacing={4}>
      <SimpleGrid columns={7} spacing={2} fontSize="sm" textTransform="uppercase" color={dayTextSubtle}>
        {weekdayLabelsCs.map((d) => (
          <Box key={d} textAlign="center">{d}</Box>
        ))}
      </SimpleGrid>
      <VStack spacing={2} align="stretch">
        {weeks.map((week, wi) => (
          <HStack key={wi} spacing={2}>
            {week.map((day) => {
              const isOutside = !isSameMonth(day, monthStart);
              const isSelectedStart = draftFrom && isSameDay(day, draftFrom);
              const isSelectedEnd = draftTo && isSameDay(day, draftTo);
              const inRange = currentRange ? isWithinInterval(day, currentRange) : false;

              let bg: string | undefined;
              let color: string | undefined;
              let roundedLeft = false;
              let roundedRight = false;

              if (inRange) {
                bg = dayBgInRange;
              }
              if (isSelectedStart || isSelectedEnd) {
                bg = dayBgSelected;
                color = dayTextSelected;
                roundedLeft = true;
                roundedRight = true;
              }

              const baseColor = isOutside ? dayTextSubtle : undefined;

              return (
                <Button
                  key={day.toISOString()}
                  size={size}
                  variant={bg ? 'solid' : 'ghost'}
                  flex="1"
                  borderRadius={bg ? (roundedLeft && roundedRight ? 'full' : 'md') : 'md'}
                  bg={bg}
                  color={color || baseColor}
                  _hover={{ bg: bg || dayBgInRange }}
                  onClick={() => handleDayClick(day)}
                  onMouseEnter={() => setHoverDate(day)}
                  onMouseLeave={() => setHoverDate(null)}
                  w="100%"
                  h="2.5rem"
                  px={0}
                >
                  {format(day, 'd')}
                </Button>
              );
            })}
          </HStack>
        ))}
      </VStack>
    </VStack>
  );

  const renderYearPanel = () => (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between">
        <Button
          size="xs"
          variant="ghost"
          leftIcon={<ChevronLeftIcon />}
          onClick={() => setYearPageStart((prev) => prev - 12)}
        >
          Starší
        </Button>
        <Text fontSize="sm" fontWeight="semibold">
          {yearOptions[0]} – {yearOptions[yearOptions.length - 1]}
        </Text>
        <Button
          size="xs"
          variant="ghost"
          rightIcon={<ChevronRightIcon />}
          onClick={() => setYearPageStart((prev) => prev + 12)}
        >
          Novější
        </Button>
      </HStack>
      <SimpleGrid columns={3} spacing={2}>
        {yearOptions.map((year) => (
          <Button
            key={year}
            size={size}
            variant={year === selectedYear ? 'solid' : 'ghost'}
            colorScheme={year === selectedYear ? 'blue' : undefined}
            onClick={() => {
              const next = setYear(viewDate, year);
              setViewDate(next);
              setActivePanel('month');
            }}
          >
            {year}
          </Button>
        ))}
      </SimpleGrid>
    </VStack>
  );

  const renderMonthPanel = () => (
    <SimpleGrid columns={3} spacing={2}>
      {monthNamesCs.map((name, idx) => (
        <Button
          key={name}
          size={size}
          variant={idx === selectedMonth ? 'solid' : 'ghost'}
          colorScheme={idx === selectedMonth ? 'blue' : undefined}
          onClick={() => {
            const next = setMonth(viewDate, idx);
            setViewDate(startOfMonth(next));
            setActivePanel('calendar');
          }}
        >
          {`${idx + 1}. ${name}`}
        </Button>
      ))}
    </SimpleGrid>
  );

  const renderDayPanel = () => (
    <SimpleGrid columns={7} spacing={2}>
      {Array.from({ length: daysInSelectedMonth }).map((_, i) => {
        const dayNum = i + 1;
        const d = new Date(selectedYear, selectedMonth, dayNum);
        const isSelectedStart = draftFrom && isSameDay(d, draftFrom);
        const isSelectedEnd = draftTo && isSameDay(d, draftTo);
        const inRange = currentRange ? isWithinInterval(d, currentRange) : false;

        let bg: string | undefined;
        let color: string | undefined;
        if (inRange) bg = dayBgInRange;
        if (isSelectedStart || isSelectedEnd) {
          bg = dayBgSelected;
          color = dayTextSelected;
        }

        return (
          <Button
            key={dayNum}
            size={size}
            variant={bg ? 'solid' : 'ghost'}
            bg={bg}
            color={color}
            _hover={{ bg: bg || dayBgInRange }}
            onClick={() => handleDayClick(d)}
            borderRadius="full"
            w="100%"
            h="2.5rem"
            px={0}
          >
            {dayNum}
          </Button>
        );
      })}
    </SimpleGrid>
  );

  const headerYear = viewDate.getFullYear();
  const headerMonthName = monthNamesCs[viewDate.getMonth()];

  return (
    <Popover
      placement="bottom-start"
      onOpen={() => {
        const start = parseDate(from);
        const end = parseDate(to);
        setDraftFrom(start);
        setDraftTo(end);
        const base = start || end || new Date();
        setViewDate(base);
        setYearPageStart(base.getFullYear() - 6);
        setActivePanel('calendar');
      }}
    >
      <PopoverTrigger>
        <Button
          size={size}
          variant="outline"
          leftIcon={<CalendarIcon />}
          borderRadius="full"
          px={5}
          py={3}
          minW={{ base: '220px', md: '260px', lg: '300px' }}
          bg={triggerBg}
          borderColor={triggerBorderColor}
          boxShadow="sm"
          _hover={{ bg: triggerHoverBg, boxShadow: 'md' }}
          overflow="hidden"
        >
          <HStack spacing={3} align="center" w="100%" justify="space-between">
            <Text fontSize="sm" color="gray.600" flexShrink={0}>
              Období:
            </Text>
            <Text
              fontSize="sm"
              fontWeight="medium"
              flex="1"
              minW={0}
              textAlign="right"
              isTruncated
              noOfLines={1}
            >
              {rangeLabel}
            </Text>
          </HStack>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        w={{ base: '100%', sm: '360px', md: '520px' }}
        maxW="520px"
        borderRadius="xl"
        boxShadow="xl"
        bg={cardBg}
        borderColor={borderColor}
      >
        <PopoverArrow />
        <PopoverBody p={0}>
          <Stack
            direction={{ base: 'column', md: 'row' }}
            spacing={0}
            divider={<Divider orientation="vertical" display={{ base: 'none', md: 'block' }} />}
          >
            <Box
              w={{ base: '100%', md: '200px' }}
              borderBottomWidth={{ base: '1px', md: 0 }}
              bg={sideBg}
              p={4}
            >
              <VStack align="stretch" spacing={2}>
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.key}
                    size="sm"
                    justifyContent="flex-start"
                    variant="ghost"
                    borderRadius="md"
                    px={3}
                    py={2}
                    fontSize="sm"
                    _hover={{ bg: sideActiveBg, color: sideActiveColor }}
                    onClick={() => {
                      const r = preset.getRange();
                      setDraftFrom(parseDate(r.from));
                      setDraftTo(parseDate(r.to));
                    }}
                  >
                    {preset.label}
                  </Button>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  borderRadius="md"
                  px={3}
                  py={2}
                  fontSize="sm"
                  onClick={clearDraft}
                >
                  Vymazat
                </Button>
              </VStack>
            </Box>

            <Box flex="1" p={4}>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" align="center">
                  <HStack spacing={2}>
                    <Button
                      size="xs"
                      borderRadius="full"
                      variant={activePanel === 'year' ? 'solid' : 'outline'}
                      colorScheme={activePanel === 'year' ? 'blue' : undefined}
                      onClick={() => setActivePanel('year')}
                    >
                      {headerYear}
                    </Button>
                    <Button
                      size="xs"
                      borderRadius="full"
                      variant={activePanel === 'month' ? 'solid' : 'outline'}
                      colorScheme={activePanel === 'month' ? 'blue' : undefined}
                      onClick={() => setActivePanel('month')}
                    >
                      {headerMonthName}
                    </Button>
                    <Button
                      size="xs"
                      borderRadius="full"
                      variant={activePanel === 'day' ? 'solid' : 'outline'}
                      colorScheme={activePanel === 'day' ? 'blue' : undefined}
                      onClick={() => setActivePanel('day')}
                    >
                      {headerDayLabel}
                    </Button>
                  </HStack>
                  <HStack spacing={1}>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setViewDate((prev) => subMonths(prev, 1))}
                    >
                      <ChevronLeftIcon />
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setViewDate((prev) => addMonths(prev, 1))}
                    >
                      <ChevronRightIcon />
                    </Button>
                  </HStack>
                </HStack>

                {activePanel === 'calendar' && renderCalendar()}
                {activePanel === 'year' && renderYearPanel()}
                {activePanel === 'month' && renderMonthPanel()}
                {activePanel === 'day' && renderDayPanel()}

                {activePanel !== 'calendar' && (
                  <Button
                    size="xs"
                    alignSelf="flex-start"
                    variant="link"
                    onClick={() => setActivePanel('calendar')}
                  >
                    Zpět na kalendář
                  </Button>
                )}

                <HStack justify="space-between" pt={2}>
                  <Text fontSize="xs" color="gray.500">
                    Rozsah:{' '}
                    {draftFrom && draftTo
                      ? `${format(draftFrom, 'd.M.yyyy', { locale: cs })} – ${format(draftTo, 'd.M.yyyy', { locale: cs })}`
                      : draftFrom
                      ? `Od ${format(draftFrom, 'd.M.yyyy', { locale: cs })}`
                      : draftTo
                      ? `Do ${format(draftTo, 'd.M.yyyy', { locale: cs })}`
                      : 'nenastaveno'}
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearDraft}
                    >
                      Zrušit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={applyDraft}
                      isDisabled={!draftFrom && !draftTo}
                    >
                      Použít
                    </Button>
                  </HStack>
                </HStack>
              </VStack>
            </Box>
          </Stack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
